import type { ThemeColors } from '@/domain/models/theme.js';

export type ClientResponse = {
    id: string;
    name: string;
    abbreviation: string;
    logoUrl: string | null;
    activeProjects: number;
    slaHoursEstimateEf: number;
    slaHoursStartDev: number;
    slaHoursAnalyzeBug: number;
    slaHoursAnalyzeMinor: number;
    slaDaysValidateDoc: number;
    slaMinScoreAi: number;
    aiModel: string;
    aiStrictnessLevel: number;
    aiExtraSystemPrompt: string;
    emailDomains: string[];
    themeColors: ThemeColors | null;
    createdAt: string;
    updatedAt: string;
    createdBy: string | null;
    environmentsErp?: ClientErpEnvResponse[];
    environmentsBtp?: ClientBtpEnvResponse[];
    documents?: ClientDocResponse[];
};

export type ClientErpEnvResponse = {
    id: string;
    clientId: string;
    description: string;
    systemId: string;
    connectionAddress: string;
    instanceNumber: string;
    createdAt: string;
    updatedAt: string;
};

export type ClientBtpEnvResponse = {
    id: string;
    clientId: string;
    description: string;
    region: string;
    subaccountId: string;
    organization: string;
    space: string;
    hanaCloudUrl: string | null;
    createdAt: string;
    updatedAt: string;
};

export type ClientDocResponse = {
    id: string;
    clientId: string;
    category: string;
    fileName: string;
    fileUrl: string;
    fileSizeBytes: string | null;
    mimeType: string | null;
    uploadedAt: string;
    uploadedBy: string | null;
    uploadedByName: string | null;
};

export type ClientProps = {
    id: string;
    name: string;
    abbreviation: string;
    logoUrl: string | null;
    activeProjects: number;
    slaHoursEstimateEf: number;
    slaHoursStartDev: number;
    slaHoursAnalyzeBug: number;
    slaHoursAnalyzeMinor: number;
    slaDaysValidateDoc: number;
    slaMinScoreAi: number;
    aiModel: string;
    aiStrictnessLevel: number;
    aiExtraSystemPrompt: string;
    emailDomains: string[];
    themeColors: ThemeColors | null;
    createdAt: string;
    updatedAt: string;
    createdBy: string | null;
};

export class ClientModel {
    private constructor(private readonly props: ClientProps) {}

    public static with(props: ClientProps): ClientModel {
        return new ClientModel(props);
    }

    public static withFromResponse(response: ClientResponse): ClientModel {
        return ClientModel.with({
            id: response.id,
            name: response.name,
            abbreviation: response.abbreviation,
            logoUrl: response.logoUrl,
            activeProjects: response.activeProjects ?? 0,
            slaHoursEstimateEf: response.slaHoursEstimateEf,
            slaHoursStartDev: response.slaHoursStartDev,
            slaHoursAnalyzeBug: response.slaHoursAnalyzeBug,
            slaHoursAnalyzeMinor: response.slaHoursAnalyzeMinor,
            slaDaysValidateDoc: response.slaDaysValidateDoc,
            slaMinScoreAi: response.slaMinScoreAi,
            aiModel: response.aiModel,
            aiStrictnessLevel: response.aiStrictnessLevel ?? 5,
            aiExtraSystemPrompt: response.aiExtraSystemPrompt ?? '',
            emailDomains: response.emailDomains ?? [],
            themeColors: response.themeColors ?? null,
            createdAt: response.createdAt,
            updatedAt: response.updatedAt,
            createdBy: response.createdBy
        });
    }

    public static withFromResponseList(list: ClientResponse[]): ClientModel[] {
        return list.map(ClientModel.withFromResponse);
    }

    public get id(): string {
        return this.props.id;
    }

    public get name(): string {
        return this.props.name;
    }

    public get abbreviation(): string {
        return this.props.abbreviation;
    }

    public get logoUrl(): string | null {
        return this.props.logoUrl;
    }

    public get activeProjects(): number {
        return this.props.activeProjects;
    }

    public get slaHoursEstimateEf(): number {
        return this.props.slaHoursEstimateEf;
    }

    public get slaHoursStartDev(): number {
        return this.props.slaHoursStartDev;
    }

    public get slaHoursAnalyzeBug(): number {
        return this.props.slaHoursAnalyzeBug;
    }

    public get slaHoursAnalyzeMinor(): number {
        return this.props.slaHoursAnalyzeMinor;
    }

    public get slaDaysValidateDoc(): number {
        return this.props.slaDaysValidateDoc;
    }

    public get slaMinScoreAi(): number {
        return this.props.slaMinScoreAi;
    }

    public get aiModel(): string {
        return this.props.aiModel;
    }

    public get aiStrictnessLevel(): number {
        return this.props.aiStrictnessLevel;
    }

    public get aiExtraSystemPrompt(): string {
        return this.props.aiExtraSystemPrompt;
    }

    public get emailDomains(): string[] {
        return this.props.emailDomains;
    }

    public get themeColors(): ThemeColors | null {
        return this.props.themeColors;
    }

    public get createdAt(): string {
        return this.props.createdAt;
    }

    public get updatedAt(): string {
        return this.props.updatedAt;
    }

    public get createdBy(): string | null {
        return this.props.createdBy;
    }

    public toObject(): ClientProps {
        return { ...this.props };
    }
}

export type ClientDetailResponse = ClientResponse & {
    environmentsErp: ClientErpEnvResponse[];
    environmentsBtp: ClientBtpEnvResponse[];
    documents: ClientDocResponse[];
};

export type ClientDetailProps = ClientProps & {
    environmentsErp: ClientErpEnvResponse[];
    environmentsBtp: ClientBtpEnvResponse[];
    documents: ClientDocResponse[];
};

export class ClientDetailModel {
    private constructor(private readonly props: ClientDetailProps) {}

    public static with(props: ClientDetailProps): ClientDetailModel {
        return new ClientDetailModel(props);
    }

    public static withFromResponse(response: ClientDetailResponse): ClientDetailModel {
        return ClientDetailModel.with({
            ...ClientModel.withFromResponse(response).toObject(),
            environmentsErp: response.environmentsErp ?? [],
            environmentsBtp: response.environmentsBtp ?? [],
            documents: response.documents ?? []
        });
    }

    public get id(): string {
        return this.props.id;
    }

    public get name(): string {
        return this.props.name;
    }

    public get abbreviation(): string {
        return this.props.abbreviation;
    }

    public get logoUrl(): string | null {
        return this.props.logoUrl;
    }

    public get activeProjects(): number {
        return this.props.activeProjects;
    }

    public get slaHoursEstimateEf(): number {
        return this.props.slaHoursEstimateEf;
    }

    public get slaHoursStartDev(): number {
        return this.props.slaHoursStartDev;
    }

    public get slaHoursAnalyzeBug(): number {
        return this.props.slaHoursAnalyzeBug;
    }

    public get slaHoursAnalyzeMinor(): number {
        return this.props.slaHoursAnalyzeMinor;
    }

    public get slaDaysValidateDoc(): number {
        return this.props.slaDaysValidateDoc;
    }

    public get slaMinScoreAi(): number {
        return this.props.slaMinScoreAi;
    }

    public get aiModel(): string {
        return this.props.aiModel;
    }

    public get aiStrictnessLevel(): number {
        return this.props.aiStrictnessLevel;
    }

    public get aiExtraSystemPrompt(): string {
        return this.props.aiExtraSystemPrompt;
    }

    public get emailDomains(): string[] {
        return this.props.emailDomains;
    }

    public get themeColors(): ThemeColors | null {
        return this.props.themeColors;
    }

    public get createdAt(): string {
        return this.props.createdAt;
    }

    public get updatedAt(): string {
        return this.props.updatedAt;
    }

    public get createdBy(): string | null {
        return this.props.createdBy;
    }

    public get environmentsErp(): ClientErpEnvResponse[] {
        return this.props.environmentsErp;
    }

    public get environmentsBtp(): ClientBtpEnvResponse[] {
        return this.props.environmentsBtp;
    }

    public get documents(): ClientDocResponse[] {
        return this.props.documents;
    }

    public toObject(): ClientDetailProps {
        return { ...this.props };
    }
}

export type CreateClientParams = {
    name: string;
    abbreviation: string;
    logoUrl?: string;
    aiModel?: string;
    slaHoursEstimateEf?: number;
    slaHoursStartDev?: number;
    slaHoursAnalyzeBug?: number;
    slaHoursAnalyzeMinor?: number;
    slaDaysValidateDoc?: number;
    slaMinScoreAi?: number;
    aiStrictnessLevel?: number;
    aiExtraSystemPrompt?: string;
    emailDomains?: string[];
    themeColors?: ThemeColors | null;
};

export type UpdateClientParams = {
    name: string;
    abbreviation: string;
    logoUrl?: string;
    aiModel?: string;
    slaHoursEstimateEf?: number;
    slaHoursStartDev?: number;
    slaHoursAnalyzeBug?: number;
    slaHoursAnalyzeMinor?: number;
    slaDaysValidateDoc?: number;
    slaMinScoreAi?: number;
    aiStrictnessLevel?: number;
    aiExtraSystemPrompt?: string;
    emailDomains?: string[];
    themeColors?: ThemeColors | null;
};

export type CreateErpEnvParams = {
    description: string;
    systemId: string;
    connectionAddress: string;
    instanceNumber: string;
};

export type UpdateErpEnvParams = {
    description?: string;
    systemId?: string;
    connectionAddress?: string;
    instanceNumber?: string;
};

export type UploadClientDocumentParams = {
    category: string;
    file: File;
};

export type CreateBtpEnvParams = {
    description: string;
    region: string;
    subaccountId: string;
    organization: string;
    space: string;
    hanaCloudUrl?: string | null;
};

export type UpdateBtpEnvParams = {
    description?: string;
    region?: string;
    subaccountId?: string;
    organization?: string;
    space?: string;
    hanaCloudUrl?: string | null;
};
